import api from "@/lib/api";
import { BusinessType, IndustryFields } from "@/components/trades/BusinessType";
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, HardHat } from "lucide-react";
import { CompanyRegisterPicker } from "@/components/CompanyRegisterPicker";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Register() {
  const nav = useNavigate();
  const [mode,setMode]=useState('CIVIL_CONSTRUCTION'),[industry,setIndustry]=useState(null),[other,setOther]=useState(''),[options,setOptions]=useState(null);
  const submitting=useRef(false);
  useEffect(()=>{const controller=new AbortController();api.get("/auth/signup-options",{signal:controller.signal,__flSkipReadCache:true}).then(({data})=>{if(!controller.signal.aborted)setOptions(data);}).catch(()=>{if(!controller.signal.aborted)setOptions({trades_available:false});});return ()=>controller.abort();},[]);
  const { register } = useAuth();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const [company, setCompanyState] = useState({ name: "", nzbn: null, manual: false });
  const [pending, setPending] = useState(null);
  const setCompany = (c) => { setCompanyState(c); setForm((f) => ({ ...f, company_name: c.name })); };

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if(submitting.current)return;
    if(!mode){setErr("Choose your business type.");return;}
    if(mode === "TRADES_SERVICES" && !options?.trades_available){setErr("Trades & Services is coming soon. Signups are not open yet.");return;}
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setErr("Please complete the 'I am human' check before continuing.");
      return;
    }
    if (!company.nzbn && !company.manual) {
      setErr("Pick your business from the NZ register list, or tap \"Can't find your business?\".");
      return;
    }
    submitting.current=true;
    setBusy(true);
    setErr("");
    const r = await register({ ...form, nzbn: company.nzbn || undefined, operating_profile:{version:1,mode}, industry:mode==='TRADES_SERVICES'?industry:null, industry_other:mode==='TRADES_SERVICES'?other:'', turnstile_token: turnstileToken });
    submitting.current=false;
    setBusy(false);
    if (r.ok && r.pending) {
      setPending(r.data);
    } else if (r.ok) {
      toast.success(`Welcome aboard, ${form.company_name || "let's go"}`);
      nav("/app");
    } else {
      setErr(r.error);
      // Reset turnstile on error so user can resolve a fresh challenge
      try { turnstileRef.current?.reset(); } catch {}
      setTurnstileToken("");
    }
  };

  if (pending) {
    return (
      <div data-testid="register-pending" className="min-h-screen bg-[#F5F7FA] text-[#142635] flex items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-xl bg-white border border-slate-200 p-8 shadow-sm">
          <div className="label-tag !text-[#FF5F15] mb-3">/ Almost there</div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight">We're checking {pending.company_name}</h1>
          <p className="mt-3 text-slate-600 text-sm" data-testid="register-pending-message">{pending.message}</p>
          <Link to="/" className="mt-6 inline-flex h-11 items-center px-5 rounded-md bg-[#FF5F15] text-white font-semibold" data-testid="register-pending-home">Back to Fieldlink</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#F5F7FA] text-[#142635]">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-xl rounded-xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-[#FF5F15] flex items-center justify-center">
              <HardHat className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl">FIELDLINK<span className="text-[#FF5F15]">.CO.NZ</span></span>
          </Link>
          <div className="label-tag !text-[#FF5F15] mb-3">/ Sign up your company</div>
          <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">Create your company account</h1>
          <p className="text-slate-600 mt-2 text-sm">7 days free with every feature unlocked. No card. Cancel anytime. You'll be the admin and can invite your team after sign-up.</p>

          <div className="mt-8 space-y-5">
            <BusinessType value={mode} tradesAvailable={!!options?.trades_available} onChange={value=>{setMode(value);setIndustry(null);setOther('');}}/>
            {mode==='TRADES_SERVICES'&&<IndustryFields industry={industry} other={other} onChange={(value,text)=>{setIndustry(value);setOther(text);}}/>}
            <div>
              <Label className="label-tag !text-slate-600">Company name</Label>
              <CompanyRegisterPicker value={company} onChange={setCompany} />
            </div>
            <div>
              <Label className="label-tag !text-slate-600">Your name</Label>
              <Input required value={form.name} onChange={set("name")} className="mt-2 h-12 rounded-md bg-white border-slate-300 text-[#142635] placeholder:text-slate-400" placeholder="Hone Te Rangi" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="label-tag !text-slate-600">Work email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} className="mt-2 h-12 rounded-md bg-white border-slate-300 text-[#142635] placeholder:text-slate-400" placeholder="you@company.co.nz" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="label-tag !text-slate-600">Password</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={set("password")} className="mt-2 h-12 rounded-md bg-white border-slate-300 text-[#142635] placeholder:text-slate-400" placeholder="6+ characters" data-testid="register-password-input" />
            </div>
            {err && (
              <div className="border border-red-300 bg-red-50 text-red-700 p-3 text-sm" data-testid="register-error">{err}</div>
            )}
            {TURNSTILE_SITE_KEY && (
              <div data-testid="register-turnstile">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setTurnstileToken("")}
                  onExpire={() => setTurnstileToken("")}
                  options={{ theme: "light", size: "flexible" }}
                />
                <p className="mt-2 text-xs text-zinc-500 italic" data-testid="register-turnstile-hint">
                  Keeping bots off the worksite — Kiwi tradies only.
                </p>
              </div>
            )}
            <Button
              type="submit"
              disabled={busy || !mode || (TURNSTILE_SITE_KEY && !turnstileToken)}
              className="w-full h-12 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
              data-testid="register-submit-button"
            >
              {busy ? "Creating..." : <>Create company account <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
            <p className="text-[10px] text-zinc-500 leading-relaxed" data-testid="register-legal-consent">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="underline hover:text-[#FF5F15]">Terms of service</Link> and{" "}
              <Link to="/privacy" className="underline hover:text-[#FF5F15]">Privacy policy</Link>.{" "}
              Your data stays in your org — we never share it with other Fieldlink customers or sell it to anyone. See our{" "}
              <Link to="/security" className="underline hover:text-[#FF5F15]">Security page</Link> for the technical detail.
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF5F15] font-bold hover:underline" data-testid="register-login-link">
              Sign in →
            </Link>
          </div>
        </form>
      </div>


    </div>
  );
}
